import { XMarkdown } from '@ant-design/x-markdown';
import { Alert, App, Skeleton, Button } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WebSocketEventTypeEnum } from '../../enums/WebSocketEventTypeEnum';
import type { WebSocketMessage } from '../../interfaces/WebSocketMessage';
import { FileService } from '../../services/fileApiService';
import { useAppStore } from '../../store/store';
import {
  registerOpenAiSummaryView,
  unregisterOpenAiSummaryView,
} from './aiSummaryViewRegistry';

interface Props {
  file: {
    id: number;
  };
  onSummaryGenerated: () => void;
  /** Closes the hosting view (modal/right panel); enables the "close and get notified" action. */
  onRequestClose?: () => void;
}

const fileService = new FileService();

/**
 * AI-generated file summary display component.
 * Handles the entire fetch/render lifecycle for an AI file summary.
 */
export const AiSummary = ({ file, onSummaryGenerated, onRequestClose }: Props) => {
  const { notification } = App.useApp();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true); // Track loading state for skeleton UI
  const [isGenerating, setIsGenerating] = useState(false); // Async generation in progress (server returned no content yet)
  const [markdownSummary, setMarkdownSummary] = useState<string>(''); // AI summary in markdown

  /**
   * Populate summary UI after successful fetch, signal event to parent, and stop loading indicator.
   * @param content - The AI-generated file summary string (markdown).
   */
  const processSummaryContent = (content: string) => {
    setMarkdownSummary(content);
    setIsGenerating(false);
    onSummaryGenerated();
    setLoading(false);
  };

  /**
   * Fetch AI summary for current file, handle errors appropriately.
   * If summary is available as string, render it immediately.
   * An empty response means generation was kicked off asynchronously: keep the
   * loading state and flag it, the websocket "summary" event triggers the re-fetch.
   * On any error, display notification.
   */
  const callAiSummary = (forceRegenerate?: boolean) => {
    if (forceRegenerate) {
      setLoading(true);
    }
    fileService
      .aiSummary(file.id, forceRegenerate)
      .then((res) => {
        if (typeof res === 'string' && res.trim() !== '') {
          processSummaryContent(res);
        } else {
          setIsGenerating(true);
        }
      })
      .catch(() => {
        notification.error({
          title: t('errors.genericFetchError', {
            resource: t('common.file'),
          }),
        });
        setLoading(false);
      });
  };

  const handleRegenerate = () => {
    callAiSummary(true);
  };

  /**
   * Effect lifecycle:
   * - Subscribe to websocket events for the whole component lifetime, so asynchronous
   *   results are never missed (first generation, regenerate, or events racing the
   *   initial fetch).
   * - Fetch AI summary immediately; if not ready, the websocket event triggers the re-fetch.
   */
  useEffect(() => {
    // WebSocket event handler: triggers AI summary re-fetch on "summary" event for right file id.
    const handleWebSocketEvent = (event: WebSocketMessage) => {
      const d = event?.data as { event_type?: string; file_id?: number } | undefined;
      const eventType = typeof event?.type === 'string' ? event.type.trim().toLowerCase() : '';
      const eventSubtype = typeof d?.event_type === 'string' ? d.event_type.trim().toLowerCase() : '';
      if (
        eventType === WebSocketEventTypeEnum.AiEvent &&
        eventSubtype === WebSocketEventTypeEnum.Summary &&
        d?.file_id === file.id
      ) {
        callAiSummary();
      }
    };

    const unsubscribeWebSocketEvent = useAppStore
      .getState()
      .events.on.webSocketEvent(handleWebSocketEvent);

    // Mark this file's summary view as open so the global toast is suppressed.
    registerOpenAiSummaryView(file.id);

    // Attempt to fetch summary immediately.
    callAiSummary();

    // Cleanup function: Unsubscribe from websocket listener on component unmount or id change.
    return () => {
      unregisterOpenAiSummaryView(file.id);
      unsubscribeWebSocketEvent();
    };
  }, [file.id, onSummaryGenerated, t]);

  return (
    <div className="min-w-screen-lg p-4">
      {loading ? (
        // Show animated skeleton UI while loading summary
        <div className="flex w-full flex-col items-center gap-2">
          {isGenerating && (
            <Alert
              type="info"
              showIcon
              className="bg-azure-light! border-azure/40! mb-3 w-full rounded-lg! px-4! py-3!"
              title={
                <span className="text-blue-dark font-medium">
                  {t('aiSummary.generationInProgress')}
                </span>
              }
              action={
                onRequestClose && (
                  <Button type="primary" size="small" className="ml-3!" onClick={onRequestClose}>
                    {t('aiSummary.closeAndNotify')}
                  </Button>
                )
              }
            />
          )}
          <Skeleton active paragraph={{ rows: 6 }} />
          <Skeleton active paragraph={{ rows: 4 }} />
          <Skeleton active paragraph={{ rows: 3 }} />
        </div>
      ) : (
        <>
          <div className="mb-2 flex justify-end">
            <Button icon={<ReloadOutlined />} onClick={handleRegenerate} size="small">
              {t('common.regenerate')}
            </Button>
          </div>
          <div className="max-h-[calc(100vh-100px)] overflow-y-auto pr-2">
            <XMarkdown>{markdownSummary}</XMarkdown>
          </div>
        </>
      )}
    </div>
  );
};

export default AiSummary;
